import { Vue, Component, Prop } from "vue-property-decorator";
import { AxiosInstance, AxiosResponse, AxiosError } from "axios";

interface AttestationOption {
  challenge: string;
  rp: {
    id: string;
    name: string;
  };
  user: {
    id: string;
    name: string;
    displayName: string;
  };
  pubKeyCredParams: PublicKeyCredentialParameters[];
  attestation: AttestationConveyancePreference;
  authenticatorSelection: {
    residentKey?: "discouraged" | "preferred" | "required";
    authenticatorAttachment: AuthenticatorAttachment;
    userVerification: UserVerificationRequirement;
  };
  excludeCredentials?: AllowCredentials[];
  timeout: number;
}

interface AssertionOption {
  allowCredentials: AllowCredentials[];
  challenge: string;
  rpId: string;
  userVerification: UserVerificationRequirement;
  timeout: number;
}

interface AllowCredentials {
  id: string;
  transports?: AuthenticatorTransport[];
  type: PublicKeyCredentialType;
}

/**
 * v-data-tableのフィルタMixin
 */
@Component
export default class extends Vue {
  /** EwellUserId */
  @Prop() ewellUserId!: number;

  /** ログインID */
  protected userId = "";

  /** パスワード */
  protected password = "";

  /** キーコード */
  private keyCode = "";

  /** 端末表示名（端末制限：登録時に保存） */
  protected deviceName = "";

  /** 端末追加キー（端末制限：厳格運用モード アクティベーションコード） */
  protected activationCode = "";

  /** ローディングフラグ */
  protected login_loading = false;

  /** エラーメッセージ */
  protected errorMessages = "";

  /** ログイン処理 */
  protected login(
    baseUrl: string,
    axios: AxiosInstance,
    fingerprint: string,
    setToken: (res: AxiosResponse) => void,
    openAlertDialog: (message: string) => void
  ) {
    this.login_loading = true;
    axios
      .post(baseUrl + "/auth/login", {
        user_id: this.userId,
        password: this.password,
        device_hash: fingerprint
      })
      .then((res: AxiosResponse) => {
        this.login_loading = false;
        setToken(res);
      })
      .catch(async (error: AxiosError) => {
        this.login_loading = false;
        this.password = "";
        if (error.response && error.response.status == 401) {
          this.errorMessages = error.response.data.message;
        } else {
          openAlertDialog("サーバでエラーが発生しました。\n(" + error + ")");
        }
      });
  }

  /** キーコードでのログイン */
  protected loginKeyCode(
    baseUrl: string,
    axios: AxiosInstance,
    query_hash: string,
    setToken: (res: AxiosResponse) => void,
    openAlertDialog: (message: string) => void
  ) {
    this.login_loading = true;
    axios
      .post(baseUrl + "/auth/double-login", {
        key_code: this.keyCode,
        token: query_hash
      })
      .then((res: AxiosResponse) => {
        this.login_loading = false;
        setToken(res);
      })
      .catch(async (error: AxiosError) => {
        this.login_loading = false;
        if (
          error.response &&
          (error.response.status == 401 || error.response.data.code == 101)
        ) {
          this.errorMessages = error.response.data.message;
        } else {
          openAlertDialog("サーバでエラーが発生しました。\n(" + error + ")");
        }
      });
  }

  /**
   * 端末制限：ログインゲート（WebAuthnアサーション）
   * ログイン(need_device_gate)後に呼ぶ。許可済み資格情報でのアサーションが成功すれば
   * サーバがJWTを返す（セルフ登録は自動許可）。パスキー未登録(begin=101)の場合は
   * onNoPasskey を呼び、登録フロー(deviceRegisterBegin)へ誘導する。
   */
  protected deviceGateBegin(
    baseUrl: string,
    axios: AxiosInstance,
    token: string,
    onComplete: (res: AxiosResponse) => void,
    onNoPasskey: () => void,
    openAlertDialog: (message: string) => void
  ) {
    this.login_loading = true;
    axios
      .post(baseUrl + "/webauthn/assertion/begin", {
        ewell_user_id: this.ewellUserId,
        token
      })
      .then((res: AxiosResponse) => {
        const option: AssertionOption = res.data.webauthn_response;
        return navigator.credentials.get({
          publicKey: {
            allowCredentials: option.allowCredentials.map(credential => {
              return {
                id: this.base64StringToArrayBuffer(credential.id),
                transports: credential.transports,
                type: credential.type
              };
            }),
            challenge: this.base64StringToArrayBuffer(option.challenge),
            rpId: option.rpId,
            timeout: option.timeout,
            userVerification: option.userVerification
          }
        });
      })
      .then(credential => {
        if (credential === null) {
          this.login_loading = false;
          openAlertDialog("認証に失敗しました");
          return;
        }
        this.deviceGateComplete(
          baseUrl,
          axios,
          token,
          onComplete,
          openAlertDialog,
          credential as PublicKeyCredential
        );
      })
      .catch((error: AxiosError & Error) => {
        this.login_loading = false;
        // ユーザーがキャンセル
        if (error.name === "NotAllowedError") {
          return;
        }
        // パスキー未登録（サーバ: 生体認証情報なし）→ 登録フローへ
        if (error.response && error.response.data.code == 101) {
          onNoPasskey();
          return;
        }
        if (error.response && error.response.status == 401) {
          this.errorMessages = error.response.data.message;
        } else {
          openAlertDialog("サーバでエラーが発生しました。\n(" + error + ")");
        }
      });
  }

  private deviceGateComplete(
    baseUrl: string,
    axios: AxiosInstance,
    token: string,
    onComplete: (res: AxiosResponse) => void,
    openAlertDialog: (message: string) => void,
    credential: PublicKeyCredential
  ) {
    const assertionResponse = credential.response as AuthenticatorAssertionResponse;
    let userHandle = "";
    if (assertionResponse.userHandle) {
      userHandle = this.arrayBufferToBase64(assertionResponse.userHandle);
    }
    const assertion = {
      id: credential.id,
      rawId: this.arrayBufferToBase64(credential.rawId),
      type: credential.type,
      response: {
        authenticatorData: this.arrayBufferToBase64(
          assertionResponse.authenticatorData
        ),
        clientDataJSON: this.arrayBufferToBase64(
          assertionResponse.clientDataJSON
        ),
        signature: this.arrayBufferToBase64(assertionResponse.signature),
        userHandle
      }
    };
    axios
      .post(baseUrl + "/webauthn/assertion/complete", {
        assertion,
        ewell_user_id: this.ewellUserId,
        token
      })
      .then((res: AxiosResponse) => {
        this.login_loading = false;
        onComplete(res);
      })
      .catch(async (error: AxiosError) => {
        this.login_loading = false;
        if (error.response && error.response.data.code == 101) {
          this.errorMessages = error.response.data.message;
        } else {
          openAlertDialog("サーバでエラーが発生しました。\n(" + error + ")");
        }
      });
  }

  /**
   * 端末制限：未ログインでのパスキー登録（attestation）
   * セルフ登録／厳格A(activationCode指定)／厳格B(activationCode空=申請)で共通。
   * onComplete にサーバレスポンス（JWT or device_request_accepted 等）を渡す。
   */
  protected deviceRegisterBegin(
    baseUrl: string,
    axios: AxiosInstance,
    token: string,
    activationCode: string,
    onComplete: (res: AxiosResponse) => void,
    openAlertDialog: (message: string) => void
  ) {
    this.login_loading = true;
    axios
      .post(baseUrl + "/auth/device-webauthn/attestation/begin", { token })
      .then((res: AxiosResponse) => {
        const option: AttestationOption = res.data.webauthn_response;
        let excludeCredentials: PublicKeyCredentialDescriptor[] = [];
        if (option.excludeCredentials !== undefined) {
          excludeCredentials = option.excludeCredentials.map(cr => {
            return {
              id: this.base64StringToArrayBuffer(cr.id),
              transports: cr.transports,
              type: cr.type
            } as PublicKeyCredentialDescriptor;
          });
        }
        return navigator.credentials.create({
          publicKey: {
            challenge: this.base64StringToArrayBuffer(option.challenge),
            rp: { id: option.rp.id, name: option.rp.name },
            user: {
              id: this.base64StringToArrayBuffer(option.user.id),
              name: option.user.name,
              displayName: option.user.displayName
            },
            excludeCredentials,
            pubKeyCredParams: option.pubKeyCredParams,
            attestation: option.attestation,
            timeout: option.timeout,
            authenticatorSelection: {
              userVerification: option.authenticatorSelection.userVerification,
              authenticatorAttachment:
                option.authenticatorSelection.authenticatorAttachment
            }
          }
        });
      })
      .then(credential => {
        if (credential === null) {
          this.login_loading = false;
          openAlertDialog("登録に失敗しました");
          return;
        }
        this.deviceRegisterComplete(
          baseUrl,
          axios,
          token,
          activationCode,
          onComplete,
          openAlertDialog,
          credential as PublicKeyCredential
        );
      })
      .catch((e: AxiosError & Error) => {
        this.login_loading = false;
        if (e.name === "NotAllowedError") {
          // キャンセル
          return;
        }
        if (e.name === "InvalidStateError") {
          openAlertDialog("登録済みのデバイスの可能性があります");
        } else if (e.response) {
          openAlertDialog("サーバでエラーが発生しました。\n(" + e + ")");
        } else {
          openAlertDialog("登録に失敗しました");
        }
      });
  }

  private deviceRegisterComplete(
    baseUrl: string,
    axios: AxiosInstance,
    token: string,
    activationCode: string,
    onComplete: (res: AxiosResponse) => void,
    openAlertDialog: (message: string) => void,
    credential: PublicKeyCredential
  ) {
    const attestationResponse = credential.response as AuthenticatorAttestationResponse;
    const attestation = {
      id: credential.id,
      rawId: this.arrayBufferToBase64(credential.rawId),
      type: credential.type,
      response: {
        attestationObject: this.arrayBufferToBase64(
          attestationResponse.attestationObject
        ),
        clientDataJSON: this.arrayBufferToBase64(
          attestationResponse.clientDataJSON
        )
      }
    };
    axios
      .post(baseUrl + "/auth/device-webauthn/attestation/complete", {
        token,
        attestation,
        activation_code: activationCode
      })
      .then((res: AxiosResponse) => {
        this.login_loading = false;
        onComplete(res);
      })
      .catch(async (error: AxiosError) => {
        this.login_loading = false;
        if (error.response && error.response.data.code == 101) {
          this.errorMessages = error.response.data.message;
        } else {
          openAlertDialog("サーバでエラーが発生しました。\n(" + error + ")");
        }
      });
  }

  /** 生体認証の開始 */
  protected loginWebauthnBegin(
    baseUrl: string,
    axios: AxiosInstance,
    query_hash: string,
    successFunc: (res: AxiosResponse) => void,
    openAlertDialog: (message: string) => void
  ) {
    this.login_loading = true;
    axios
      .post(baseUrl + "/webauthn/assertion/begin", {
        ewell_user_id: this.ewellUserId
      })
      .then((res: AxiosResponse) => {
        const option: AssertionOption = res.data.webauthn_response;
        return navigator.credentials.get({
          publicKey: {
            allowCredentials: option.allowCredentials.map(credential => {
              return {
                id: this.base64StringToArrayBuffer(credential.id),
                transports: credential.transports,
                type: credential.type
              };
            }),
            challenge: this.base64StringToArrayBuffer(option.challenge),
            rpId: option.rpId,
            timeout: option.timeout,
            userVerification: option.userVerification
          }
        });
      })
      .then(credential => {
        if (credential !== null) {
          this.loginWebauthnComplete(
            baseUrl,
            axios,
            query_hash,
            successFunc,
            openAlertDialog,
            credential as PublicKeyCredential
          );
        } else {
          openAlertDialog("認証に失敗しました");
        }
      })
      .catch((error: AxiosError) => {
        this.login_loading = false;
        //キャンセルの場合
        if (error.name === "NotAllowedError") {
          return;
        }
        if (
          error.response &&
          (error.response.status == 401 || error.response.data.code == 101)
        ) {
          this.errorMessages = error.response.data.message;
        } else {
          openAlertDialog("サーバでエラーが発生しました。\n(" + error + ")");
        }
      });
  }

  /** 生体認証の完了 */
  private loginWebauthnComplete(
    baseUrl: string,
    axios: AxiosInstance,
    query_hash: string,
    successFunc: (res: AxiosResponse) => void,
    openAlertDialog: (message: string) => void,
    credential: PublicKeyCredential
  ) {
    const assertionResponse = credential.response as AuthenticatorAssertionResponse;
    let userHandle = "";
    if (assertionResponse.userHandle) {
      userHandle = this.arrayBufferToBase64(assertionResponse.userHandle);
    }
    const assertion = {
      id: credential.id,
      rawId: this.arrayBufferToBase64(credential.rawId),
      type: credential.type,
      response: {
        authenticatorData: this.arrayBufferToBase64(
          assertionResponse.authenticatorData
        ),
        clientDataJSON: this.arrayBufferToBase64(
          assertionResponse.clientDataJSON
        ),
        signature: this.arrayBufferToBase64(assertionResponse.signature),
        userHandle
      }
    };

    axios
      .post(baseUrl + "/webauthn/assertion/complete", {
        assertion,
        ewell_user_id: this.ewellUserId,
        token: query_hash
      })
      .then((res: AxiosResponse) => {
        this.login_loading = false;
        successFunc(res);
      })
      .catch(async (error: AxiosError) => {
        this.login_loading = false;
        if (error.response && error.response.data.code == 101) {
          this.errorMessages = error.response.data.message;
        } else {
          await openAlertDialog(
            "サーバでエラーが発生しました。\n(" + error + ")"
          );
        }
      });
  }

  /** 生体認証の登録開始 */
  protected registerWebauthnBegin(
    baseUrl: string,
    postJsonCheck: (
      url: string,
      param: unknown,
      sucessFunc: (res: AxiosResponse) => void,
      catchFunc?: (error: AxiosError) => void
    ) => void,
    openAlertDialog: (message: string) => void,
    updateDataFunc: () => void = () => {
      return;
    },
    cancelFunc: () => void = () => {
      return;
    },
    isDeviceRestriction = false,
    invalidStateFunc?: () => void
  ) {
    this.login_loading = true;
    postJsonCheck(
      baseUrl + "/api/webauthn/attestation/begin",
      {},
      async res => {
        const option: AttestationOption = res.data.webauthn_response;

        let excludeCredentials: PublicKeyCredentialDescriptor[] = [];
        if (option.excludeCredentials !== undefined) {
          excludeCredentials = option.excludeCredentials.map(c => {
            return {
              id: this.base64StringToArrayBuffer(c.id),
              transports: c.transports,
              type: c.type
            } as PublicKeyCredentialDescriptor;
          });
        }

        navigator.credentials
          .create({
            publicKey: {
              challenge: this.base64StringToArrayBuffer(option.challenge),
              rp: {
                id: option.rp.id,
                name: option.rp.name
              },
              user: {
                id: this.base64StringToArrayBuffer(option.user.id),
                name: option.user.name,
                displayName: option.user.displayName
              },
              excludeCredentials,
              pubKeyCredParams: option.pubKeyCredParams,
              attestation: option.attestation,
              timeout: option.timeout,
              authenticatorSelection: {
                userVerification:
                  option.authenticatorSelection.userVerification,
                authenticatorAttachment:
                  option.authenticatorSelection.authenticatorAttachment
              }
            }
          })
          .then(c => {
            if (c !== null) {
              this.registerWebauthnComplete(
                baseUrl,
                postJsonCheck,
                openAlertDialog,
                c as PublicKeyCredential,
                updateDataFunc,
                isDeviceRestriction
              );
            }
          })
          .catch((e: Error) => {
            this.login_loading = false;
            if (e.name === "NotAllowedError") {
              // キャンセルの可能性あり
              // do nothing
              cancelFunc();
            } else if (e.name === "InvalidStateError") {
              if (invalidStateFunc) {
                invalidStateFunc();
              } else {
                openAlertDialog("登録済みのデバイスの可能性があります");
              }
            } else {
              openAlertDialog("登録に失敗しました");
            }
          });
      }
    );
  }

  /** 生体認証の登録完了 */
  private registerWebauthnComplete(
    baseUrl: string,
    postJsonCheck: (
      url: string,
      param: unknown,
      sucessFunc: (res: AxiosResponse) => void,
      catchFunc?: (error: AxiosError) => void
    ) => void,
    openAlertDialog: (message: string) => void,
    credential: PublicKeyCredential,
    updateDataFunc: () => void = () => {
      return;
    },
    isDeviceRestriction = false
  ) {
    const attestationResponse = credential.response as AuthenticatorAttestationResponse;
    const data = {
      id: credential.id,
      rawId: this.arrayBufferToBase64(credential.rawId),
      type: credential.type,
      response: {
        attestationObject: this.arrayBufferToBase64(
          attestationResponse.attestationObject
        ),
        clientDataJSON: this.arrayBufferToBase64(
          attestationResponse.clientDataJSON
        )
      }
    };

    const completeUrl =
      baseUrl +
      "/api/webauthn/attestation/complete" +
      (isDeviceRestriction ? "?register_authorized_device=1" : "");

    postJsonCheck(
      completeUrl,
      data,
      async () => {
        this.login_loading = false;
        await openAlertDialog("登録しました");
        updateDataFunc();
      },
      () => {
        this.login_loading = false;
        openAlertDialog("登録失敗");
      }
    );
  }

  private arrayBufferToBase64(buffer: ArrayBuffer) {
    const binary = String.fromCharCode(...new Uint8Array(buffer));
    return window
      .btoa(binary)
      .replace(/\//g, "_")
      .replace(/\+/g, "-")
      .replace(/=+$/g, "");
  }

  private base64StringToArrayBuffer(base64String: string) {
    const replaced = base64String.replace(/_/g, "/").replace(/-/g, "+");
    const decoded = window.atob(replaced);
    return Uint8Array.from(decoded, c => c.charCodeAt(0));
  }
}
